import React, { useEffect, useState } from "react";
import { PolicyAdditionalInterestDto } from "../../../model/PolicyAdditionalInterestDto";
import { useSelector } from "react-redux";
import { CustomerPolicyDetailLabels as cstPolicyAdditionalInterestsLabels } from '../../../../shared/util/label';
import { FloatingLabel, Form } from "react-bootstrap";
import { RootState } from "../../../../shared/store";
import { Column, Modal, Row, Select } from "../../../../components";
import * as constant from '../../../../shared/util/constant';
import CustomerDetailService from "../../../customer-details/CustomerDetailService";
import TextInputWithZipCode from "../../../../shared/components/input/TextInputWithZipCode";
import { mortgageService } from "../../../../Maintenance/Mortgage/MortgageService";
import './CustomerAdditionalInterests.scss';

export interface ICustomerAdditionalInterestsFormProps {
    interest: PolicyAdditionalInterestDto;
    onEntityFieldChange: (propName: string, value: string | boolean) => void;
    onEntityFieldsChange?: (updatedFields: Partial<PolicyAdditionalInterestDto>) => void;
    tabIndex?: number;
}

const CustomerAdditionalInterestsForm: React.FC<ICustomerAdditionalInterestsFormProps> = ({
    interest,
    onEntityFieldChange,
    onEntityFieldsChange,
    tabIndex,
}) => {
    interface AdditionalInterestedPartyRecord {
        id: string;
        interestTypeId: string;
        name1: string;
        name2: string;
        address1: string;
        address2: string;
        city: string;
        state: string;
        zip: string;
    }

    interface MortgageCompanyRecord {
        id?: string;
        mortgageCompanyId?: string;
        interestTypeId?: string;
        name1?: string;
        name2?: string;
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zip?: string;
        zipCode?: string;
        companyName?: string;
        name?: string;
    }

    interface InterestTypePopupState {
        show: boolean;
        interestTypeId: string;
        filteredLookupRecords: AdditionalInterestedPartyRecord[];
        selectedLookupRecordId: string;
        selectedRecord: AdditionalInterestedPartyRecord | null;
        isLookupLoading: boolean;
    }

    let tabIndexLocal = tabIndex;
    const customerpolicyDetail = useSelector((state: RootState) => state?.customerSlice);
    const selectedLocationDetails = customerpolicyDetail.selectedLocationDetails;
    const stateUSLookup = useSelector((state: RootState) => state?.LookupState.stateUSList);
    const attachTypeLookup = useSelector((state: RootState) => state?.LookupState.attachTypeList);
    const interestTypeLookup = useSelector((state: RootState) => state?.LookupState.interestTypeList) || [];

    const [formValidation, setFormValidation] = useState<any>({});
    const customerDetailService = new CustomerDetailService();

    // Interest Type popup state
    const [popupData, setPopupData] = useState<InterestTypePopupState>({
        show: false,
        interestTypeId: "",
        filteredLookupRecords: [],
        selectedLookupRecordId: "",
        selectedRecord: null,
        isLookupLoading: false
    });

    const allowedAttachTypes = [
        constant.CS2_AttachTypes.LOCATION,
        constant.CS2_AttachTypes.LINE_OF_BUSINESS
    ];
    // Filter attachTypes
    let filteredAttachTypes = [];
    if (!selectedLocationDetails) {
        // Only allow LOB if no location is selected
        filteredAttachTypes = attachTypeLookup.filter(
            option => option.value === constant.CS2_AttachTypes.LINE_OF_BUSINESS
        );
    } else {
        // Allow both LOCATION and LOB
        filteredAttachTypes = attachTypeLookup.filter(
            option => allowedAttachTypes.includes(option.value)
        );
    }
    const attachTypes = [...filteredAttachTypes];
    const states = [...stateUSLookup];
    const handleChangeZipCodeOnBlur = (propName: string, val: string) => {
        if (val) {
            let validationErrors = customerDetailService.validationForZipCode(propName, val)
            if (Object.keys(validationErrors).length > 0) {
                setFormValidation({ ...formValidation, ...validationErrors })
            } else {
                const newObject = { ...formValidation };
                delete newObject[propName];
                setFormValidation(newObject);
            }
        }
    };

    const handleName1SearchClick = () => {
        setPopupData(prev => ({
            ...prev,
            show: true,
            interestTypeId: interest.interestTypeId || "",
            selectedLookupRecordId: "",
            selectedRecord: null
        }));
    };

    const normalizeLookupValue = (value: any): string => {
        if (value === null || value === undefined) {
            return "";
        }
        return String(value).trim();
    };

    const resolveInterestTypeId = (value: string): string => {
        if (!value) {
            return "";
        }
        const matched = interestTypeLookup.find(option =>
            option.value === value ||
            option.label?.toString().toLowerCase() === value.toLowerCase()
        );
        return matched?.value || "";
    };

    const mapMortgageCompanyToRecord = (item: MortgageCompanyRecord, selectedInterestTypeId: string): AdditionalInterestedPartyRecord | null => {
        const name1Value = normalizeLookupValue(item.name1 || item.companyName || item.name);
        const mappedInterestType = normalizeLookupValue(item.interestTypeId);
        const resolvedInterestType = resolveInterestTypeId(mappedInterestType) || mappedInterestType || selectedInterestTypeId;

        const mapped: AdditionalInterestedPartyRecord = {
            id: normalizeLookupValue(item.id || item.mortgageCompanyId || name1Value),
            interestTypeId: resolvedInterestType,
            name1: name1Value,
            name2: normalizeLookupValue(item.name2),
            address1: normalizeLookupValue(item.address1),
            address2: normalizeLookupValue(item.address2),
            city: normalizeLookupValue(item.city),
            state: normalizeLookupValue(item.state),
            zip: normalizeLookupValue(item.zip || item.zipCode)
        };

        if (!mapped.name1 && !mapped.name2 && !mapped.address1 && !mapped.address2 && !mapped.city && !mapped.state && !mapped.zip) {
            return null;
        }

        return mapped;
    };

const fetchAdditionalInterestedPartyRecords = async (interestTypeId: string) => {
        setPopupData(prev => ({ ...prev, isLookupLoading: true }));
        try {
            const response = await mortgageService.mortgageCompanyByInterestType(interestTypeId);
            const mappedRecords = response
                .map((item) => mapMortgageCompanyToRecord(item as MortgageCompanyRecord, interestTypeId))
                .filter((item): item is AdditionalInterestedPartyRecord => item !== null);

            setPopupData(prev => ({ ...prev, filteredLookupRecords: mappedRecords }));
        } catch {
            setPopupData(prev => ({ ...prev, filteredLookupRecords: [] }));
        } finally {
            setPopupData(prev => ({ ...prev, isLookupLoading: false }));
        }
    };

    const nameLookupOptions = popupData.filteredLookupRecords.map((record) => {
        const displayName = record.name1 || record.name2 || `${record.city || ""} ${record.state || ""}`.trim() || "(Unnamed)";
        return {
            label: displayName,
            value: record.id
        };
    });

    const handlePopupNameChange = (selectedId: string) => {
        const selectedRecord = popupData.filteredLookupRecords.find((record) => record.id === selectedId) || null;
        setPopupData(prev => ({
            ...prev,
            selectedLookupRecordId: selectedId,
            selectedRecord
        }));
    };

    const handleInterestTypePopupSave = () => {
        const selectedRecord = popupData.selectedRecord || ({} as AdditionalInterestedPartyRecord);
        const { id: _selectedId, interestTypeId: _selectedInterestTypeId, ...selectedRecordFields } = selectedRecord;

        const updatedFields: Partial<PolicyAdditionalInterestDto> = {
            interestTypeId: popupData.interestTypeId,
            ...selectedRecordFields
        };

        onEntityFieldsChange?.(updatedFields);

        setPopupData(prev => ({ ...prev, show: false }));
    };

    const handleInterestTypePopupCancel = () => {
        setPopupData(prev => ({ ...prev, show: false }));
    };

    useEffect(() => {
        if (!popupData.show || !popupData.interestTypeId) {
            return;
        }
        setPopupData(prev => ({
            ...prev,
            selectedLookupRecordId: "",
            selectedRecord: null
        }));
        fetchAdditionalInterestedPartyRecords(popupData.interestTypeId);
    }, [popupData.interestTypeId, popupData.show]);
    
    return (
        <>
        <div className="additional-interest-form" style={{ marginBottom: 12 }}>
            {/* Row 1: Attach Type | Interest Type | Name 1 | Name 2 */}
            <Row className="mb-3">
                <Column lg={3} md={3}>
                    <Select
                        tabIndex={tabIndexLocal}
                        id="attachTypeId"
                        controlId="attachTypeId"
                        label={cstPolicyAdditionalInterestsLabels.ATTACH_TYPE}
                        options={attachTypes}
                        defaultSelected={attachTypes.filter(option =>
                            option.value === interest.attachTypeId
                        )}
                        onChange={(values) => onEntityFieldChange('attachTypeId', values[0] as string)}
                        className='mb-3'
                        disabled={attachTypes.length === 1}
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        tabIndex={tabIndexLocal}
                        id="interestTypeId"
                        controlId="interestTypeId"
                        label={cstPolicyAdditionalInterestsLabels.INTEREST_TYPE}
                        placeholder={constant.PLEASE_SELECT}
                        options={interestTypeLookup}
                        defaultSelected={interestTypeLookup.filter(option =>
                            option.value === interest.interestTypeId
                        )}
                        onChange={(values) => onEntityFieldChange('interestTypeId', values[0] as string)}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <div className="name1-search-wrapper">
                        <FloatingLabel controlId="name1" label={cstPolicyAdditionalInterestsLabels.NAME1}>
                            <Form.Control
                                type="text"
                                tabIndex={tabIndexLocal}
                                value={interest.name1 || ""}
                                onChange={e => onEntityFieldChange("name1", e.target.value)}
                                maxLength={51}
                                className="name1-search-input"
                            />
                        </FloatingLabel>
                        <button
                            type="button"
                            className="name1-search-btn"
                            onClick={handleName1SearchClick}
                            tabIndex={tabIndexLocal}
                            aria-label="Open interest type popup"
                        >
                            <i className="bi bi-search"></i>
                        </button>
                    </div>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="name2" label={cstPolicyAdditionalInterestsLabels.NAME2}>
                        <Form.Control
                            type="text"
                            tabIndex={tabIndexLocal}
                            value={interest.name2 || ""}
                            onChange={e => onEntityFieldChange("name2", e.target.value)}
                            maxLength={51}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
            {/* Row 2: Address 1 | Address 2 | City | State */}
            <Row className="mb-3">
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="address1" label={cstPolicyAdditionalInterestsLabels.ADDRESS1}>
                        <Form.Control
                            type="text"
                            tabIndex={tabIndexLocal}
                            value={interest.address1 || ""}
                            onChange={e => onEntityFieldChange("address1", e.target.value)}
                            maxLength={30}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="address2" label={cstPolicyAdditionalInterestsLabels.ADDRESS2}>
                        <Form.Control
                            type="text"
                            tabIndex={tabIndexLocal}
                            value={interest.address2 || ""}
                            onChange={e => onEntityFieldChange("address2", e.target.value)}
                            maxLength={30}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="city" label={cstPolicyAdditionalInterestsLabels.CITY}>
                        <Form.Control
                            type="text"
                            tabIndex={tabIndexLocal}
                            value={interest.city || ""}
                            onChange={e => onEntityFieldChange("city", e.target.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="state"
                        controlId="state"
                        label={cstPolicyAdditionalInterestsLabels.STATE}
                        tabIndex={tabIndexLocal}
                        options={states}
                        defaultSelected={states.filter(option =>
                            option.value === interest.state
                        )}
                        onChange={(values) => onEntityFieldChange('state', values[0] as string)}
                        className='mb-3'
                    />
                </Column>
            </Row>
            {/* Row 3: ZIP Code | Loan Number | Rank | Payor */}
            <Row className="mb-3">
                <Column lg={3} md={3}>
                    <TextInputWithZipCode
                        name="zip"
                        label="Zip"
                        controlId="zip"
                        tabIndex={tabIndexLocal}
                        placeholder="Zip"
                        value={interest.zip ?? ""}
                        onChange={(event) => {
                            onEntityFieldChange('zip', event.currentTarget.value);
                        }}
                        onBlur={() => handleChangeZipCodeOnBlur('zip', interest.zip)}
                        disabled={false}
                        maxLength={10}
                        type={"text"}
                        isInvalid={formValidation['zip']?.isValid == false}
                        invalidWarning={formValidation['zip']?.warningText}
                    />
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="referenceNumber" label={cstPolicyAdditionalInterestsLabels.LOAN_NUMBER}>
                        <Form.Control
                            type="text"
                            tabIndex={tabIndexLocal}
                            value={interest.referenceNumber || ""}
                            onChange={e => onEntityFieldChange("referenceNumber", e.target.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="rank" label={cstPolicyAdditionalInterestsLabels.RANK}>
                        <Form.Control
                            type="number"
                            tabIndex={tabIndexLocal}
                            value={interest.rank || ""}
                            min="1"
                            max="9999"
                            onChange={e => {
                                let value = e.target.value.replace(/[^0-9]/g, '');
                                if (value && parseInt(value) > 9999) {
                                    value = '9999';
                                }
                                onEntityFieldChange("rank", value);
                            }}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3} className="d-flex align-items-center payor-column">
                    <Form.Check
                        type="checkbox"
                        id="isPayor"
                        tabIndex={tabIndexLocal}
                        label={cstPolicyAdditionalInterestsLabels.PAYOR}
                        checked={interest.isPayor}
                        onChange={e => onEntityFieldChange("isPayor", e.target.checked)}
                    />
                </Column>
            </Row>
            {/* Row 4: Business Phone | Fax | Email 1 | Email 2 */}
            <Row className="mb-3">
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="businessPhone" label={cstPolicyAdditionalInterestsLabels.BUSINESS_PHONE}>
                        <Form.Control
                            type="text"
                            tabIndex={tabIndexLocal}
                            value={interest.businessPhone || ""}
                            onChange={e => onEntityFieldChange("businessPhone", e.target.value)}
                            maxLength={20}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="fax" label={cstPolicyAdditionalInterestsLabels.FAX}>
                        <Form.Control
                            type="text"
                            tabIndex={tabIndexLocal}
                            value={interest.fax || ""}
                            onChange={e => onEntityFieldChange("fax", e.target.value)}
                            maxLength={20}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="email1" label={cstPolicyAdditionalInterestsLabels.EMAIL_1}>
                        <Form.Control
                            type="email"
                            tabIndex={tabIndexLocal}
                            value={interest.email || ""}
                            onChange={e => onEntityFieldChange("email", e.target.value)}
                            maxLength={100}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="email2" label={cstPolicyAdditionalInterestsLabels.EMAIL_2}>
                        <Form.Control
                            type="email"
                            tabIndex={tabIndexLocal}
                            value={interest.email2 || ""}
                            onChange={e => onEntityFieldChange("email2", e.target.value)}
                            maxLength={100}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
        </div>

        <Modal
            show={popupData.show}
            toggle={handleInterestTypePopupCancel}
            title={cstPolicyAdditionalInterestsLabels.INTEREST_TYPE}
            className="additional-interest-popup-modal"
            verticallyCentered={true}
            backdrop="static"
            showHeaderCloseButton={true}
            body={
                <div>
                    <Row className="mb-3">
                        <Column lg={12}>
                            {popupData.isLookupLoading && (
                                <div className="text-muted small">Loading records...</div>
                            )}
                            {!popupData.isLookupLoading && popupData.filteredLookupRecords.length === 0 && (
                                <div className="text-muted small">No records found for selected Interest Type.</div>
                            )}
                        </Column>
                    </Row>
                    <Row className="mb-3">
                        <Column lg={6}>
                            <Select
                                key={`popup-name-${popupData.interestTypeId}-${popupData.selectedLookupRecordId}-${nameLookupOptions.length}`}
                                id="popup-nameLookupId"
                                controlId="popup-nameLookupId"
                                label={cstPolicyAdditionalInterestsLabels.NAME1}
                                options={nameLookupOptions}
                                defaultSelected={nameLookupOptions.filter(option => option.value === popupData.selectedLookupRecordId)}
                                onChange={(values) => handlePopupNameChange(values[0] as string)}
                                className='mb-3'
                                disabled={popupData.isLookupLoading || nameLookupOptions.length === 0}
                            />
                        </Column>
                        <Column lg={6}>
                            <FloatingLabel controlId="popup-name2" label={cstPolicyAdditionalInterestsLabels.NAME2}>
                                <Form.Control
                                    type="text"
                                    value={popupData.selectedRecord?.name2 || ""}
                                    maxLength={51}
                                    disabled
                                />
                            </FloatingLabel>
                        </Column>
                    </Row>
                    <Row className="mb-3">
                        <Column lg={6}>
                            <FloatingLabel controlId="popup-address1" label={cstPolicyAdditionalInterestsLabels.ADDRESS1}>
                                <Form.Control
                                    type="text"
                                    value={popupData.selectedRecord?.address1 || ""}
                                    maxLength={30}
                                    disabled
                                />
                            </FloatingLabel>
                        </Column>
                        <Column lg={6}>
                            <FloatingLabel controlId="popup-address2" label={cstPolicyAdditionalInterestsLabels.ADDRESS2}>
                                <Form.Control
                                    type="text"
                                    value={popupData.selectedRecord?.address2 || ""}
                                    maxLength={30}
                                    disabled
                                />
                            </FloatingLabel>
                        </Column>
                    </Row>
                    <Row className="mb-3">
                        <Column lg={4}>
                            <FloatingLabel controlId="popup-city" label={cstPolicyAdditionalInterestsLabels.CITY}>
                                <Form.Control
                                    type="text"
                                    value={popupData.selectedRecord?.city || ""}
                                    disabled
                                />
                            </FloatingLabel>
                        </Column>
                        <Column lg={4}>
                            <Select
                                key={`popup-state-${popupData.selectedRecord?.state || ""}`}
                                id="popup-state"
                                controlId="popup-state"
                                label={cstPolicyAdditionalInterestsLabels.STATE}
                                options={states}
                                defaultSelected={states.filter(option =>
                                    option.value === (popupData.selectedRecord?.state || "")
                                )}
                                className='mb-3'
                                disabled
                            />
                        </Column>
                        <Column lg={4}>
                            <TextInputWithZipCode
                                name="popup-zip"
                                label={cstPolicyAdditionalInterestsLabels.ZIP_CODE}
                                controlId="popup-zip"
                                placeholder={cstPolicyAdditionalInterestsLabels.ZIP_CODE}
                                value={popupData.selectedRecord?.zip || ""}
                                disabled
                                maxLength={10}
                                type="text"
                            />
                        </Column>
                    </Row>
                </div>
            }
            closeButton={{
                size: "small",
                icon: "cancel",
                iconStyle: { color: "gray" },
                outlined: true,
                color: 'link',
                onClick: handleInterestTypePopupCancel,
                children: <span className="closeButton">CANCEL</span>
            }}
            successButton={{
                size: "small",
                icon: "check",
                iconStyle: { color: "177db8" },
                outlined: true,
                color: 'link',
                onClick: handleInterestTypePopupSave,
                children: <span className="successButton">SELECT</span>
            }}
            buttonsAlignment="right"
        />
        </>
    );
};

export default CustomerAdditionalInterestsForm;

